import type {
  CaptureItem,
  CaptureInput,
  CaptureToken,
  CaptureDestination,
  CaptureXPreview,
  PackageOperations,
  CreateDropChannel,
  DigestReport,
  DropChannel,
  FeedbackEventAction,
  OverlayItem,
  PodcastEpisodeDetail,
  PodcastAnalytics,
  PodcastFeedKind,
  PodcastLibrary,
  PodcastSettings,
  SetPodcastVoiceResult,
  SearchQuery,
  SearchResult,
  ResearchComment,
  ResearchKind,
  ResearchLinkRef,
  ResearchStatus,
  ResearchDefaultView,
  ResearchVisibility,
  SourceRef,
  CreateNoteInput,
  CreateTodo,
  InputsSnapshot,
  Note,
  SweepContext,
  SweepKind,
  Todo,
  TodoEvent,
  TodoProposal,
  UpdateNoteInput,
  UpdateDropChannel,
  UpdateTodo,
} from '@almanac/contracts';
import { webConfig } from '../config';

export interface DigestSummary {
  date: string;
  title: string | null;
  generated_at: string;
  refreshed_at: string | null;
  sources: string[];
  urgent_total: number;
  urgent_unaddressed: number;
}

export interface ResearchListItem {
  research_id: string;
  owner_user_id: string;
  note_id: string | null;
  title: string;
  slug: string;
  summary_md: string;
  tags: string[];
  kind: ResearchKind;
  visibility: ResearchVisibility;
  /** 'poster' opens the item as its full-screen poster instead of the detail page */
  default_view: ResearchDefaultView;
  status: ResearchStatus;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  has_poster: boolean;
  /** owner's display name (falling back to their email), or null when unknown; list-only */
  owner_display_name: string | null;
}

export interface ResearchDetail extends Omit<ResearchListItem, 'owner_display_name'> {
  body_md: string;
  body_html: string | null;
  sources: SourceRef[];
  git_path: string | null;
}

/**
 * Research PATCH body. Content fields are owner-only server-side; a
 * status-only patch (archive/unarchive) is allowed for any authenticated user.
 * `html` replaces the poster document (null clears it).
 */
export interface ResearchPatch {
  title?: string;
  summary_md?: string;
  body_md?: string;
  html?: string | null;
  tags?: string[];
  visibility?: ResearchVisibility;
  status?: 'published' | 'archived';
}

/** The unauthenticated share projection served at /api/public/research/:slug. */
export interface PublicResearchItem {
  title: string;
  slug: string;
  summary_md: string;
  body_md: string;
  body_html: string | null;
  tags: string[];
  sources: SourceRef[];
  has_poster: boolean;
  /** 'poster' opens /p/:slug as the full-screen poster */
  default_view: ResearchDefaultView;
  published_at: string | null;
  updated_at: string;
}

/** Tokenless fetch for the public share page (usable outside AuthProvider). */
export async function fetchPublicResearch(slug: string): Promise<PublicResearchItem> {
  const res = await fetch(
    `${webConfig.apiBaseUrl}/api/public/research/${encodeURIComponent(slug)}`,
    {
      headers: { accept: 'application/json' },
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => res.statusText);
    throw new ApiError(res.status, detail);
  }
  const data = (await res.json()) as { item: PublicResearchItem };
  return data.item;
}

export interface BriefingResponse {
  body_md: string;
  head_sha: string;
  fetched_at: string;
}

export interface PackageDocResponse extends BriefingResponse {
  slug: string;
}

/** Cross-links an explainer doc carries outside its HTML (rendered as page chrome). */
export interface DocRelated {
  package?: string;
  topics?: string[];
  linear?: string[];
  prs?: string[];
  docs?: string[];
}

export interface DocListEntry {
  slug: string;
  title: string;
  summary: string;
  /** Sidebar placement: nested category path, outermost first. */
  category: string[];
  status: 'active' | 'draft' | 'stale' | 'archived';
  updated: string;
  related: DocRelated;
  grounding: string[];
}

export interface DocsListResponse {
  docs: DocListEntry[];
  head_sha: string;
  fetched_at: string;
}

export interface DocResponse {
  slug: string;
  meta: Omit<DocListEntry, 'slug'>;
  html: string;
  head_sha: string;
  fetched_at: string;
}

export interface MeResponse {
  sub: string;
  email: string | null;
  display_name: string | null;
  via: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type TokenGetter = () => Promise<string | null>;

export function createApiClient(getToken: TokenGetter) {
  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const headers = new Headers(init?.headers);
    headers.set('accept', 'application/json');
    const token = await getToken();
    if (token) headers.set('authorization', `Bearer ${token}`);
    if (init?.body !== undefined) headers.set('content-type', 'application/json');

    const res = await fetch(`${webConfig.apiBaseUrl}${path}`, { ...init, headers });
    if (!res.ok) {
      const detail = await res.text().catch(() => res.statusText);
      throw new ApiError(res.status, detail);
    }
    return (await res.json()) as T;
  }

  return {
    listCaptures: (before?: Pick<CaptureItem, 'created_at' | 'capture_id'>) =>
      request<{ captures: CaptureItem[] }>(
        `/api/captures${before ? `?before=${encodeURIComponent(before.created_at)}&before_id=${encodeURIComponent(before.capture_id)}` : ''}`,
      ),
    createCapture: (input: CaptureInput) =>
      request<{ capture: CaptureItem }>('/api/captures', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    fileCapture: (id: string, destination: CaptureDestination) =>
      request<{ capture: CaptureItem }>(`/api/captures/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ destination }),
      }),
    archiveCapture: (id: string) =>
      request<{ capture: CaptureItem }>(`/api/captures/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),
    restoreCapture: (id: string) =>
      request<{ capture: CaptureItem }>(`/api/captures/${encodeURIComponent(id)}/restore`, {
        method: 'POST',
      }),
    loadCapturePreview: (id: string) =>
      request<{ preview: CaptureXPreview | null }>(
        `/api/captures/${encodeURIComponent(id)}/preview`,
        { method: 'POST' },
      ),
    listCaptureTokens: () => request<{ tokens: CaptureToken[] }>('/api/captures/tokens'),
    mintCaptureToken: (label: string) =>
      request<{ token: string; record: CaptureToken }>('/api/captures/tokens', {
        method: 'POST',
        body: JSON.stringify({ label }),
      }),
    revokeCaptureToken: (id: string) =>
      request<{ revoked: boolean }>(`/api/captures/tokens/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),
    me: () => request<MeResponse>('/api/me'),
    getPackageOperations: (slug: string) =>
      request<PackageOperations>(`/api/briefing/packages/${encodeURIComponent(slug)}/operations`),
    getBriefing: () => request<BriefingResponse>('/api/briefing'),
    getBriefingPackage: (slug: string) =>
      request<PackageDocResponse>(`/api/briefing/packages/${encodeURIComponent(slug)}`),
    listDocs: () => request<DocsListResponse>('/api/docs'),
    getDoc: (slug: string) => request<DocResponse>(`/api/docs/${encodeURIComponent(slug)}`),
    getPodcast: () => request<PodcastLibrary>('/api/podcast'),
    getPodcastAnalytics: () => request<PodcastAnalytics>('/api/podcast/analytics'),
    getPodcastEpisode: (feed: PodcastFeedKind, guid: string) =>
      request<PodcastEpisodeDetail>(
        `/api/podcast/episodes/${encodeURIComponent(guid)}?feed=${encodeURIComponent(feed)}`,
      ),
    getPodcastSettings: () => request<PodcastSettings>('/api/podcast/settings'),
    /** A voice ElevenLabs says the account cannot use comes back as `rejected`, not thrown. */
    setPodcastVoice: async (voiceId: string): Promise<SetPodcastVoiceResult> => {
      try {
        return await request<SetPodcastVoiceResult>('/api/podcast/voice', {
          method: 'PUT',
          body: JSON.stringify({ voice_id: voiceId }),
        });
      } catch (e: unknown) {
        if (e instanceof ApiError && e.status === 422) {
          return JSON.parse(e.message) as SetPodcastVoiceResult;
        }
        throw e;
      }
    },
    listDigests: () => request<{ digests: DigestSummary[] }>('/api/digests'),
    getDigest: (date: string) =>
      request<{ report: DigestReport; overlay: Record<string, OverlayItem> }>(
        `/api/digests/${encodeURIComponent(date)}`,
      ),
    listResearch: (options?: { includeArchived?: boolean; search?: string }) => {
      const params = new URLSearchParams();
      if (options?.includeArchived) params.set('include_archived', '1');
      if (options?.search) params.set('q', options.search);
      const suffix = params.size > 0 ? `?${params.toString()}` : '';
      return request<{ research: ResearchListItem[] }>(`/api/research${suffix}`);
    },
    getFindingConversation: (researchId: string) =>
      request<{ conversation: { role: string; text: string }[] }>(
        `/api/research/${researchId}/conversation`,
      ),
    getResearch: (researchId: string) =>
      request<{
        item: ResearchDetail;
        comments: ResearchComment[];
        derived_from: ResearchLinkRef[];
        findings: ResearchLinkRef[];
      }>(`/api/research/${encodeURIComponent(researchId)}`),
    updateResearch: (researchId: string, patch: ResearchPatch) =>
      request<{ item: ResearchDetail }>(`/api/research/${encodeURIComponent(researchId)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    deleteResearch: (researchId: string) =>
      request<{ deleted: boolean }>(`/api/research/${encodeURIComponent(researchId)}`, {
        method: 'DELETE',
      }),
    sendFeedback: (body: {
      item_id: string;
      action: FeedbackEventAction;
      until?: string;
      text?: string;
    }) =>
      request<{ overlay: OverlayItem }>('/api/feedback', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    search: (input: SearchQuery, signal?: AbortSignal) => {
      const params = new URLSearchParams({ q: input.q, limit: String(input.limit) });
      if (input.kinds.length > 0) params.set('kinds', input.kinds.join(','));
      if (input.contentTypes.length > 0) {
        params.set('content_types', input.contentTypes.join(','));
      }
      if (input.sources.length > 0) params.set('sources', input.sources.join(','));
      if (input.statuses.length > 0) params.set('statuses', input.statuses.join(','));
      if (input.dateFrom) params.set('date_from', input.dateFrom);
      if (input.dateTo) params.set('date_to', input.dateTo);
      return request<{ results: SearchResult[]; unavailable_sources?: string[] }>(
        `/api/search?${params.toString()}`,
        { signal },
      );
    },
    listInputs: (options?: { includeArchived?: boolean; search?: string }) => {
      const params = new URLSearchParams();
      if (options?.includeArchived) params.set('include_archived', '1');
      if (options?.search) params.set('q', options.search);
      const suffix = params.size > 0 ? `?${params.toString()}` : '';
      return request<InputsSnapshot>(`/api/inputs${suffix}`);
    },
    getSweepContext: (sweep: SweepKind) =>
      request<{ context: SweepContext }>(
        `/api/inputs/sweep-context?sweep=${encodeURIComponent(sweep)}`,
      ),
    createInput: (input: CreateNoteInput) =>
      request<{ note: Note }>('/api/inputs', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    getInput: (noteId: string, signal?: AbortSignal) =>
      request<{ note: Note }>(`/api/inputs/notes/${encodeURIComponent(noteId)}`, { signal }),
    updateInput: (noteId: string, patch: UpdateNoteInput) =>
      request<{ note: Note }>(`/api/inputs/notes/${encodeURIComponent(noteId)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    archiveInput: (noteId: string) =>
      request<{ note: Note }>(`/api/inputs/notes/${encodeURIComponent(noteId)}/archive`, {
        method: 'POST',
      }),
    restoreInput: (noteId: string) =>
      request<{ note: Note }>(`/api/inputs/notes/${encodeURIComponent(noteId)}/restore`, {
        method: 'POST',
      }),
    createDropChannel: (input: CreateDropChannel) =>
      request<{ drop_channel: DropChannel }>('/api/inputs/drop-channels', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    updateDropChannel: (channelId: string, patch: UpdateDropChannel) =>
      request<{ drop_channel: DropChannel }>(
        `/api/inputs/drop-channels/${encodeURIComponent(channelId)}`,
        { method: 'PATCH', body: JSON.stringify(patch) },
      ),
    deleteDropChannel: (channelId: string) =>
      request<{ deleted: boolean }>(`/api/inputs/drop-channels/${encodeURIComponent(channelId)}`, {
        method: 'DELETE',
      }),
    createTodo: (input: CreateTodo) =>
      request<{ todo: Todo }>('/api/todos', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    getTodo: (todoId: string, signal?: AbortSignal) =>
      request<{ todo: Todo }>(`/api/todos/${encodeURIComponent(todoId)}`, { signal }),
    updateTodo: (todoId: string, patch: UpdateTodo) =>
      request<{ todo: Todo }>(`/api/todos/${encodeURIComponent(todoId)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    archiveTodo: (todoId: string) =>
      request<{ todo: Todo }>(`/api/todos/${encodeURIComponent(todoId)}/archive`, {
        method: 'POST',
      }),
    restoreTodo: (todoId: string) =>
      request<{ todo: Todo }>(`/api/todos/${encodeURIComponent(todoId)}/restore`, {
        method: 'POST',
      }),
    reorderTodos: (order: string[]) =>
      request<{ todos: Todo[] }>('/api/todos/reorder', {
        method: 'POST',
        body: JSON.stringify({ order }),
      }),
    listTodoEvents: (todoId: string) =>
      request<{ events: TodoEvent[] }>(`/api/todos/${encodeURIComponent(todoId)}/events`),
    acceptTodoProposal: (proposalId: string, edits: UpdateTodo = {}) =>
      request<{ proposal: TodoProposal; todo: Todo }>(
        `/api/todos/proposals/${encodeURIComponent(proposalId)}/accept`,
        { method: 'POST', body: JSON.stringify(edits) },
      ),
    rejectTodoProposal: (proposalId: string) =>
      request<{ proposal: TodoProposal }>(
        `/api/todos/proposals/${encodeURIComponent(proposalId)}/reject`,
        { method: 'POST' },
      ),
    restoreTodoProposal: (proposalId: string) =>
      request<{ proposal: TodoProposal }>(
        `/api/todos/proposals/${encodeURIComponent(proposalId)}/restore`,
        { method: 'POST' },
      ),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
